// frontend/src/features/ups/UpsBlockedAssignmentsList.js
// Phase 8.7B: UPS-admin exception queue for orphaned/blocked assignments.
// Reads: GET /ups/sites/:siteKey/assignments/orphaned
// Shows "blocked_reason" and allows inspection (details).
//
// Phase 8.7C:
// - Add cleanup-only actions (Complete / Cancel) for blocked/orphaned assignments.
// - After action, refetch list (reload) so resolved rows disappear.

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { Eye, AlertTriangle } from 'lucide-react';

import { selectIsUpsAdmin } from '../auth/authSlice';
import { selectAllAgencies } from '../agencies/agenciesSlice';
import { selectSelectedSiteId } from '../sites/sitesSlice';
import { selectFilters, bumpStatsRefreshKey } from '../ui/filtersSlice';

import DriverDetailsModal from './modals/DriverDetailsModal';
import { upsertDriver, selectDriverById } from '../drivers/driverSlice';

import {
  fetchOrphanedAssignments,
  completeAssignment,
  cancelAssignment,
} from '../../services/assignmentService';

const normalizeId = (v) => (v == null ? undefined : Number(v));

function reasonBadgeClass(reason) {
  const r = String(reason || '').toLowerCase();
  if (r.includes('missing contract')) return 'bg-yellow-100 text-yellow-800';
  if (r.includes('contract disabled')) return 'bg-orange-100 text-orange-800';
  if (r.includes('site not allowed')) return 'bg-red-100 text-red-800';
  if (r.includes('site disabled')) return 'bg-red-100 text-red-800';
  return 'bg-gray-100 text-gray-700';
}

/**
 * Row component so we can safely call hooks (useSelector) per-row.
 * This lets us show "Banned" badge based on the canonical driver store state.
 */
function BlockedAssignmentRow({
  driver,
  openDetails,
  onComplete,
  onCancel,
  actionBusyId,
}) {
  const bannedDriver = useSelector((state) =>
    driver.id ? selectDriverById(state, driver.id) : null
  );

  // Prefer explicit boolean if you have it; otherwise swap to banned_at/ban_reason
  const isBanned = Boolean(bannedDriver?.is_banned);

  const busy = actionBusyId === String(driver.assignment_id);

  return (
    <tr
      key={driver.assignment_id ?? driver.id}
      data-row-id={String(driver.assignment_id ?? driver.id)} // assignment-first identity
      {...(driver.id ? { 'data-driver-id': String(driver.id) } : {})} // optional driver fallback
      className="hover:bg-gray-50"
    >
      {/* Name + Banned badge */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-2">
          <span>{driver.name}</span>

          {isBanned && (
            <span className="px-2 py-0.5 text-xs rounded bg-red-100 text-red-700 font-medium">
              Banned
            </span>
          )}
        </div>
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        {driver.vehicle_registration_number || driver.vrn || '—'}
      </td>

      <td className="px-6 py-4 whitespace-nowrap">{driver.phone || '—'}</td>
      <td className="px-6 py-4 whitespace-nowrap">{driver.email || '—'}</td>

      <td className="px-6 py-4 whitespace-nowrap">
        {driver.agency_name || driver.agency?.name || '—'}
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={'text-xs px-2 py-0.5 rounded ' + reasonBadgeClass(driver.blocked_reason)}
          title={driver.blocked_reason || 'Blocked'}
        >
          {driver.blocked_reason || 'Blocked'}
        </span>
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        {driver.submission_date ? new Date(driver.submission_date).toLocaleString() : '—'}
      </td>

      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center space-x-4">
          {/* Details only (exception queue) */}
          <button
            onClick={() => openDetails(driver)}
            aria-label="View driver details"
            title="View details"
            className="p-1 hover:bg-gray-100 rounded text-blue-600"
          >
            <Eye size={16} />
          </button>

          {/* Phase 8.7C: cleanup-only actions */}
          <button
            onClick={() => onComplete(driver)}
            disabled={busy}
            className="px-2 py-1 rounded text-xs bg-green-600 text-white disabled:opacity-50"
            title={
              isBanned
                ? 'Driver is banned. Only mark Complete if the job already finished.'
                : 'Mark job as completed'
            }
          >
            Complete
          </button>

          <button
            onClick={() => onCancel(driver)}
            disabled={busy}
            className="px-2 py-1 rounded text-xs bg-red-600 text-white disabled:opacity-50"
            title="Cancel (job stopped)"
          >
            Cancel
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function UpsBlockedAssignmentsList() {
  const dispatch = useDispatch();

  const isAdmin = useSelector(selectIsUpsAdmin);
  const agencies = useSelector(selectAllAgencies);
  const { agencyId, q } = useSelector(selectFilters);
  const selectedSiteId = useSelector(selectSelectedSiteId);

  // Local state (exception queue; keep simple)
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  // Phase 8.7C: disable per-row actions while a cleanup call is in flight
  const [actionBusyId, setActionBusyId] = useState(null);

  // Phase 8.7C: Extracted reload() so:
  // - the effect can call it
  // - Complete/Cancel can call it after mutating state
  const reload = async () => {
    if (!selectedSiteId) return;
    if (!isAdmin) return;

    setLoading(true);
    setLoadErr(null);

    try {
      const data = await fetchOrphanedAssignments(Number(selectedSiteId), {
        agencyId: agencyId ? Number(agencyId) : undefined,
        search: (q || '').trim() ? q : undefined,
        // NOTE: backend defaults to ['requested','confirmed','active'] when not provided
      });

      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('fetchOrphanedAssignments failed:', e);
      setLoadErr(e?.response?.data?.error || e?.message || 'Failed to load blocked assignments');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Load orphaned rows whenever site/filters change
  useEffect(() => {
    let alive = true;

    async function run() {
      if (!alive) return;

      // Keep state consistent when there is no site or user is not admin
      if (!selectedSiteId || !isAdmin) {
        setRows([]);
        setLoading(false);
        setLoadErr(null);
        return;
      }

      await reload();
    }

    run();
    return () => {
      alive = false;
    };
  }, [selectedSiteId, isAdmin, agencyId, q]); // reload depends on these values

  // Focus-on-arrival via ?focus=<assignmentId OR driverId> (Phase 8.7B supports both)
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const focus = sp.get('focus');
    if (!focus) return;

    const el =
      document.querySelector(`[data-row-id="${focus}"]`) ||
      document.querySelector(`[data-driver-id="${focus}"]`);
    if (!el) return;

    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('ring-2', 'ring-blue-400', 'animate-pulse');

    const t = setTimeout(() => {
      el.classList.remove('ring-2', 'ring-blue-400', 'animate-pulse');
      const next = new URLSearchParams(sp);
      next.delete('focus');
      setSp(next, { replace: true });
    }, 1600);

    return () => clearTimeout(t);
  }, [sp, setSp]);

  // Adapt orphaned assignment rows into "driver-like" rows for reuse
  const drivers = useMemo(
    () =>
      (rows || []).map((r) => ({
        // driver
        id: normalizeId(r.driver_id ?? r.driverId ?? r.driverIdRaw), // ✅ driver PK (modal + driverSlice)
        assignment_id: normalizeId(r.assignment_id ?? r.assignmentId ?? r.id), // ✅ assignment PK (row identity)
        name: r.name,
        vehicle_registration_number: r.vehicle_registration_number,
        vrn: r.vrn,
        phone: r.phone,
        email: r.email,
        submission_date: r.submission_date,
        agency_id: r.agency_id,
        agency_name: r.agency_name,
        photo_url: r.photo_url,
        photo_mime: r.photo_mime,
        photo_updated_at: r.photo_updated_at,

        // assignment metadata
        assignment_status: r.assignment_status,
        assignment_type: r.assignment_type,
        start_time: r.start_time,
        end_time: r.end_time,
        created_at: r.created_at,

        // blocked
        blocked_reason: r.blocked_reason,
      })),
    [rows]
  );

  // Empty-state labels (same as your other lists)
  const scopeLabel = (() => {
    if (!agencyId) return 'All Agencies';
    const a = (agencies || []).find(
      (x) => String(x.id ?? x.agency_id ?? x.agencyId) === String(agencyId)
    );
    return a?.name ?? a?.agency_name ?? `Agency #${agencyId}`;
  })();

  const hasSearch = (q || '').trim().length > 0;

  // Details modal
  const [showDetails, setShowDetails] = useState(false);
  const [detailsDriver, setDetailsDriver] = useState(null);

  const openDetails = (driver) => {
    dispatch(upsertDriver(driver)); // prime Redux so modal can be live-updated
    setDetailsDriver(driver);
    setShowDetails(true);
  };

  const closeDetails = () => {
    setShowDetails(false);
    setDetailsDriver(null);
  };

  // Phase 8.7C: cleanup-only actions for blocked/orphaned assignments
  async function onComplete(row) {
    const aId = row.assignment_id ?? row.assignmentId ?? row.id;
    if (!aId) return;

    try {
      setActionBusyId(String(aId));
      await completeAssignment(selectedSiteId, aId);
      await reload();
      dispatch(bumpStatsRefreshKey());
    } catch (e) {
      console.error('completeAssignment failed:', e);
      setLoadErr('Failed to complete assignment');
    } finally {
      setActionBusyId(null);
    }
  }

  async function onCancel(row) {
    const aId = row.assignment_id ?? row.assignmentId ?? row.id;
    if (!aId) return;

    try {
      setActionBusyId(String(aId));
      await cancelAssignment(selectedSiteId, aId);
      await reload();
      dispatch(bumpStatsRefreshKey());
    } catch (e) {
      console.error('cancelAssignment failed:', e);
      setLoadErr('Failed to cancel assignment');
    } finally {
      setActionBusyId(null);
    }
  }

  // Guard: if not admin, show a clear message (route should already be gated, but nice UX)
  if (!isAdmin) {
    return (
      <div className="my-6">
        <h3 className="text-2xl font-bold mb-2 text-gray-800">Blocked Assignments</h3>
        <div className="bg-white rounded-lg shadow p-6 text-gray-600">
          This page is restricted to UPS admins.
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto my-6">
        <div className="flex items-center gap-3 mb-4">
          <h3 className="text-2xl font-bold text-gray-800">Blocked Assignments</h3>
          <span className="text-sm text-gray-500 flex items-center gap-1">
            <AlertTriangle size={16} />
            Exception queue — blocked assignments requiring cleanup (no operational actions)
          </span>
        </div>

        <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {[
                'Name',
                'Vehicle Reg.',
                'Phone',
                'Email',
                'Agency',
                'Reason',
                'Submitted',
                'Actions',
              ].map((col) => (
                <th
                  key={col}
                  className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                  Loading blocked assignments…
                </td>
              </tr>
            ) : loadErr ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-red-600">
                  {loadErr}
                </td>
              </tr>
            ) : drivers.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                  No blocked assignments{agencyId ? ` for ${scopeLabel}` : ''}
                  {hasSearch ? ` matching “${q}”` : ''}.
                </td>
              </tr>
            ) : (
              drivers.map((driver) => (
                <BlockedAssignmentRow
                  key={driver.assignment_id ?? driver.id}
                  driver={driver}
                  openDetails={openDetails}
                  onComplete={onComplete}
                  onCancel={onCancel}
                  actionBusyId={actionBusyId}
                />
              ))
            )}
          </tbody>
        </table>

        <div className="mt-3 text-sm text-gray-500">
          Note: Blocked rows are hidden from normal tabs because the agency has no active contract, or the
          site isn’t allowed by contract.
        </div>
      </div>

      {/* Driver Details Modal */}
      {showDetails && detailsDriver && (
        <DriverDetailsModal driver={detailsDriver} onClose={closeDetails} />
      )}
    </>
  );
}
